// A page's first-visit tour (driver.js), and the replay behind the nav bar's
// "?". Steps live in tours/tourSteps.js.
//
// Shown once per page per device, remembered in localStorage. Demo visitors and
// members are remembered separately, so someone who looked around the demo
// still gets the member tour after signing up. Who gets one without asking is
// decided in tours/tourPrefs.js: managers who predate the tours do not.
import { useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { TOURS } from "../tours/tourSteps";
import { runTour, stepPopover } from "../tours/tourDriver";
import { setActiveTour } from "../tours/activeTour";
import { hasSeen, markSeen, seenKey, toursRunUnprompted } from "../tours/tourPrefs";
import { isGuidedDemo, endGuidedDemo } from "../tours/guidedDemo";

// Pages load their data after mounting, so the first target can take a moment
// to exist. Waited for rather than started blind, up to this long.
const WAIT_MS = 8000;
const POLL_MS = 300;

// On the page and taking up room. A section that renders nothing still leaves
// its wrapper behind (the news when there is none, the top 25 before a week
// has scored), and a step pointed at that would light up an empty strip.
const isShown = (el) => Boolean(el) && el.getBoundingClientRect().height > 0;

// nextLabel / nextTo: where the guided demo tour goes after this page
// (tours/guidedDemo.js). Only used in that mode, where the last step's button
// reads nextLabel and takes the visitor to nextTo instead of closing.
// nextEnds marks the last page of the tour. Strings, not an object, so a page
// re-rendering does not rebuild the tour under a visitor mid-step.
export function usePageTour(page, { demo = false, enabled = true, nextLabel = null, nextTo = null, nextEnds = false } = {}) {
  const driverRef = useRef(null);
  const navigate = useNavigate();
  const { userData } = useAuth();
  const createdAt = userData?.created_at;

  const start = useCallback(() => {
    const guided = demo && isGuidedDemo();
    const ctx = { demo, guided };
    const steps = (TOURS[page] || [])
      // A help-only step belongs to an inline "?" (tourSteps.js), not here.
      // guidedOnly and notGuided steps exist for the guided demo tour only,
      // or for every tour but that one. demoOnly and memberOnly split the
      // same way between a signed-out visitor and a member.
      .filter((step) => !step.helpOnly
        && (guided || !step.guidedOnly)
        && !(guided && step.notGuided)
        && (demo || !step.demoOnly)
        && !(demo && step.memberOnly)
        && isShown(document.querySelector(step.el)))
      .map((step) => ({ element: step.el, popover: stepPopover(step, ctx) }));
    if (steps.length === 0) return;

    // In the guided tour the last step moves on to the next page rather than
    // closing. Any other way of ending it (the cross, Escape) ends the guided
    // tour as well: someone who closes it wants to look around on their own.
    let advancing = false;
    if (guided && nextTo) {
      const last = steps[steps.length - 1];
      last.popover = {
        ...last.popover,
        doneBtnText: nextLabel,
        onNextClick: () => {
          advancing = true;
          if (nextEnds) endGuidedDemo();
          driverRef.current?.destroy();
          navigate(nextTo);
        },
        // The tour's last word offers both ways on: sign up, or stay and
        // click around. "Keep exploring" just closes it, which also ends the guided
        // tour, leaving the visitor on the page with the "?" to replay.
        ...(nextEnds ? {
          showButtons: ["next", "close"],
          onPopoverRender: (popover) => {
            // Room for the two buttons side by side: "5 of 5" says nothing
            // the title does not. Hidden here because the step-level
            // showProgress option does not reach a step the tour-wide one
            // turned on.
            if (popover.progress) popover.progress.style.display = "none";
            const explore = document.createElement("button");
            explore.type = "button";
            explore.className = "lineup-tour-explore";
            explore.textContent = "Keep exploring";
            explore.addEventListener("click", () => driverRef.current?.destroy());
            popover.footerButtons.prepend(explore);
          },
        } : {}),
      };
    }

    driverRef.current?.destroy();
    driverRef.current = runTour(steps, {
      onDestroyed: () => { if (guided && !advancing) endGuidedDemo(); },
      // A tap on the dimmed page moves on rather than closing, in the guided
      // tour only. On a phone that tap is easy to make by accident, and here
      // it would throw away the rest of the walk-through.
      ...(guided ? { overlayClickBehavior: "nextStep" } : {}),
    });
  }, [page, demo, nextLabel, nextTo, nextEnds, navigate]);

  // Register for the nav bar's "?" and the settings menu's "Show tutorial"
  // while this page is mounted. A tour asked for counts as seen, so turning
  // the tutorial on does not show this page twice.
  useEffect(() => {
    if (!enabled) return undefined;
    setActiveTour(() => {
      markSeen(seenKey(page, demo));
      start();
    });
    return () => setActiveTour(null);
  }, [enabled, page, demo, start]);

  // First visit: wait for the first target, then run once.
  useEffect(() => {
    if (!enabled) return undefined;
    if (!toursRunUnprompted({ demo, createdAt })) return undefined;
    const key = seenKey(page, demo);
    if (hasSeen(key)) return undefined;

    const firstEl = TOURS[page]?.[0]?.el;
    let waited = 0;
    const id = setInterval(() => {
      waited += POLL_MS;
      if ((firstEl && document.querySelector(firstEl)) || waited >= WAIT_MS) {
        clearInterval(id);
        // Marked when shown, not when finished: leaving the page mid-tour
        // should not bring it back on the next visit. "?" replays it.
        markSeen(key);
        start();
      }
    }, POLL_MS);
    return () => clearInterval(id);
  }, [enabled, page, demo, createdAt, start]);

  // The overlay lives on <body>, so it would outlive a navigation away.
  useEffect(() => () => driverRef.current?.destroy(), []);

  return start;
}
